import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SignUp, useAuth } from "@clerk/react";
import { toast } from "sonner";
import { Check, Loader2, Plus, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { publicApi, useApi } from "@/lib/api";
import { Markdown } from "@/pages/site/Content";
import { track, visitorId } from "@/lib/track";

const STEPS = ["Business", "Pharmacy", "Your login", "Voucher", "Review"];
const blankBranch = { name: "", address: "", postcode: "", gphc: "", ods: "", superintendent: "", superintendent_gphc: "" };
const load = () => JSON.parse(localStorage.getItem("ir_signup") || "null");

const F = ({ label, children, hint }) => <label className="block text-sm font-semibold text-slate-700">{label}{children}{hint && <span className="mt-1 block text-xs font-normal text-slate-500">{hint}</span>}</label>;

function DocDialog({ slug, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (slug) publicApi("get", `/legal/${slug}`).then(setD); }, [slug]);
  return (
    <Dialog open={!!slug} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="contract-dialog">
        <DialogHeader><DialogTitle>{d?.title}</DialogTitle></DialogHeader>
        {d && <><p className="text-xs text-slate-500">Version {d.version} · Effective {d.effective}</p><Markdown text={d.body} /></>}
        <Button onClick={onClose} data-testid="contract-dialog-close">Close</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function SignupPage() {
  const [sp] = useSearchParams();
  const { isSignedIn } = useAuth();
  const api = useApi();
  const saved = load();
  const [step, setStep] = useState(() => { const s = STEPS.findIndex((x) => x.toLowerCase().startsWith(sp.get("step") || "~")); return !saved?.biz?.company_name ? 0 : s >= 0 ? s : saved?.step || 0; });
  const [biz, setBiz] = useState(saved?.biz || { company_name: "", company_number: "", company_type: "limited", billing_name: "", billing_email: "", vat_number: "" });
  const [branches, setBranches] = useState(saved?.branches || Array.from({ length: Math.max(1, Number(sp.get("branches")) || 1) }, () => ({ ...blankBranch })));
  const [voucher, setVoucher] = useState(sp.get("voucher") || saved?.voucher || localStorage.getItem("ir_voucher") || "");
  const [kp, setKp] = useState(null);
  const [agree, setAgree] = useState(false);
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { track("signup_started"); }, []);
  useEffect(() => { localStorage.setItem("ir_signup", JSON.stringify({ step, biz, branches, voucher })); if (voucher) localStorage.setItem("ir_voucher", voucher); else localStorage.removeItem("ir_voucher"); }, [step, biz, branches, voucher]);
  useEffect(() => {
    if (step < 3) return;
    let live = true;
    const t = setTimeout(() => publicApi("get", `/public/key-points?branches=${branches.length}&voucher=${encodeURIComponent(voucher)}&company_number=${encodeURIComponent(biz.company_number)}`).then((r) => live && setKp(r)), 300);
    return () => { live = false; clearTimeout(t); };
  }, [step, voucher, branches.length, biz.company_number]);
  useEffect(() => { if (step === 2 && isSignedIn) setStep(3); }, [step, isSignedIn]);

  const setB = (i, k, v) => setBranches(branches.map((b, j) => (j === i ? { ...b, [k]: v } : b)));
  const next = (e) => { e?.preventDefault(); if (step === 0) track("business_done"); setStep(step + 1); };

  const pay = async () => {
    setBusy(true);
    try {
      const r = await api("post", "/signup", { business: biz, branches, voucher: kp?.voucher_valid ? voucher : "", agree, key_points: kp.key_points, origin_url: window.location.origin, vid: visitorId() });
      window.location.href = r.checkout_url;
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-5 py-12">
      <ol className="mb-10 flex flex-wrap gap-2" data-testid="signup-steps">
        {STEPS.map((s, i) => <li key={s} className={`rounded-full px-3 py-1 text-xs font-semibold ${i === step ? "bg-slate-900 text-white" : i < step ? "bg-teal-100 text-teal-800" : "bg-slate-100 text-slate-500"}`}>{i < step && <Check className="mr-1 inline h-3 w-3" />}{i + 1}. {s}</li>)}
      </ol>

      {step === 0 && (
        <form onSubmit={next} className="space-y-4" data-testid="signup-business-form">
          <h1 className="font-display text-3xl font-extrabold">Your business</h1>
          <F label="Company name"><Input required value={biz.company_name} onChange={(e) => setBiz({ ...biz, company_name: e.target.value })} className="mt-1" data-testid="signup-company-name" /></F>
          <F label="Business type">
            <select value={biz.company_type} onChange={(e) => setBiz({ ...biz, company_type: e.target.value })} className="mt-1 h-10 w-full rounded-md border border-input bg-white px-3 text-sm" data-testid="signup-company-type">
              <option value="limited">Limited company</option><option value="sole_trader">Sole trader</option><option value="partnership">Partnership</option>
            </select></F>
          <F label="Company number" hint="As registered at Companies House, e.g. 01234567"><Input required={biz.company_type === "limited"} value={biz.company_number} onChange={(e) => setBiz({ ...biz, company_number: e.target.value.toUpperCase() })} className="mt-1" data-testid="signup-company-number" /></F>
          <div className="grid gap-4 sm:grid-cols-2">
            <F label="Billing contact name"><Input required value={biz.billing_name} onChange={(e) => setBiz({ ...biz, billing_name: e.target.value })} className="mt-1" data-testid="signup-billing-name" /></F>
            <F label="Billing email"><Input required type="email" value={biz.billing_email} onChange={(e) => setBiz({ ...biz, billing_email: e.target.value })} className="mt-1" data-testid="signup-billing-email" /></F>
          </div>
          <F label="VAT number (if any)"><Input value={biz.vat_number} onChange={(e) => setBiz({ ...biz, vat_number: e.target.value })} className="mt-1" data-testid="signup-vat-number" /></F>
          <Button type="submit" className="w-full" data-testid="signup-business-next">Continue</Button>
        </form>
      )}

      {step === 1 && (
        <form onSubmit={next} className="space-y-6" data-testid="signup-branch-form">
          <h1 className="font-display text-3xl font-extrabold">Your pharmacy</h1>
          {branches.map((b, i) => (
            <div key={i} className="space-y-3 rounded-3xl border border-slate-200 bg-white p-5" data-testid={`signup-branch-${i}`}>
              <div className="flex items-center justify-between"><p className="font-display font-bold">Branch {i + 1}</p>{i > 0 && <button type="button" onClick={() => setBranches(branches.filter((_, j) => j !== i))} aria-label="Remove branch" data-testid={`signup-branch-remove-${i}`}><Trash2 className="h-4 w-4 text-slate-400" /></button>}</div>
              <F label="Trading name"><Input required value={b.name} onChange={(e) => setB(i, "name", e.target.value)} className="mt-1" data-testid={`signup-branch-name-${i}`} /></F>
              <F label="Full address"><Input required value={b.address} onChange={(e) => setB(i, "address", e.target.value)} className="mt-1" data-testid={`signup-branch-address-${i}`} /></F>
              <div className="grid gap-3 sm:grid-cols-3">
                <F label="Postcode"><Input required pattern="[A-Za-z]{1,2}[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}" title="A UK postcode, e.g. NG7 2EF" value={b.postcode} onChange={(e) => setB(i, "postcode", e.target.value.toUpperCase())} className="mt-1" data-testid={`signup-branch-postcode-${i}`} /></F>
                <F label="GPhC premises no."><Input required inputMode="numeric" pattern="\d{7}" title="7 digits" value={b.gphc} onChange={(e) => setB(i, "gphc", e.target.value)} className="mt-1" data-testid={`signup-branch-gphc-${i}`} /></F>
                <F label="ODS code (optional)"><Input value={b.ods} onChange={(e) => setB(i, "ods", e.target.value.toUpperCase())} className="mt-1" data-testid={`signup-branch-ods-${i}`} /></F>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <F label="Superintendent pharmacist (optional)"><Input value={b.superintendent} onChange={(e) => setB(i, "superintendent", e.target.value)} className="mt-1" /></F>
                <F label="Superintendent GPhC no. (optional)"><Input value={b.superintendent_gphc} onChange={(e) => setB(i, "superintendent_gphc", e.target.value)} className="mt-1" /></F>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={() => setBranches([...branches, { ...blankBranch }])} data-testid="signup-add-branch"><Plus className="mr-1 h-4 w-4" />Add another branch (£89 a month)</Button>
          <div className="flex gap-3"><Button type="button" variant="ghost" onClick={() => setStep(0)}>Back</Button><Button type="submit" className="flex-1" data-testid="signup-branch-next">Continue</Button></div>
        </form>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="signup-account-step">
          <h1 className="font-display text-3xl font-extrabold">Create your owner login</h1>
          <p className="text-sm text-slate-600">We'll email a code to confirm your address. Your progress is saved.</p>
          <SignUp routing="hash" signInUrl="/sign-in" forceRedirectUrl="/signup?step=voucher" />
          <p className="text-sm">Already have a login? <Link to="/sign-in?redirect_url=/signup" className="font-semibold text-teal-700">Sign in</Link></p>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4" data-testid="signup-voucher-step">
          <h1 className="font-display text-3xl font-extrabold">Voucher code</h1>
          <p className="text-sm text-slate-600">If you were sent a code, enter it here. Otherwise skip this step.</p>
          <Input value={voucher} onChange={(e) => setVoucher(e.target.value.toUpperCase())} placeholder="IR-XXXXXXXX" data-testid="signup-voucher-input" />
          {voucher && kp && (kp.voucher_valid
            ? <p className="rounded-xl bg-teal-50 p-3 text-sm text-teal-900" data-testid="signup-voucher-ok">Your first month is free. Your card will first be charged on {kp.first_charge}.</p>
            : <p className="rounded-xl bg-rose-50 p-3 text-sm text-rose-800" data-testid="signup-voucher-error">{kp.voucher_reason}</p>)}
          <div className="flex gap-3"><Button variant="ghost" onClick={() => setStep(1)}>Back</Button><Button className="flex-1" disabled={!!voucher && kp && !kp.voucher_valid} onClick={() => setStep(4)} data-testid="signup-voucher-next">{voucher ? "Continue" : "Skip"}</Button></div>
        </div>
      )}

      {step === 4 && kp && (
        <div className="space-y-5" data-testid="signup-review-step">
          <h1 className="font-display text-3xl font-extrabold">Review and agree</h1>
          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Contract summary</p>
            <p className="mt-2 font-display text-3xl font-extrabold" data-testid="signup-total">£{kp.monthly_total}<span className="text-base font-semibold text-slate-500"> / month + VAT if applicable</span></p>
            <ul className="mt-4 space-y-2 text-sm text-slate-700" data-testid="signup-key-points">{kp.key_points.map((p) => <li key={p} className="flex gap-2"><Check className="mt-0.5 h-4 w-4 shrink-0 text-teal-700" />{p}</li>)}</ul>
          </div>
          <label className="flex items-start gap-3 rounded-2xl border border-slate-300 bg-white p-4 text-sm">
            <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" data-testid="signup-agree-checkbox" />
            <span>{kp.box} Read: {["terms", "dpa", "privacy", "aup", "cancellation", ...(kp.voucher_valid ? ["vouchers"] : [])].map((s, i) => <button key={s} type="button" onClick={() => setDoc(s)} className="mr-2 font-semibold text-teal-700 underline" data-testid={`signup-doc-${s}`}>{["Terms", "DPA", "Privacy", "Acceptable Use", "Cancellation", "Voucher Terms"][i]}</button>)}</span>
          </label>
          <div className="flex gap-3"><Button variant="ghost" onClick={() => setStep(3)}>Back</Button>
            <Button className="flex-1" disabled={!agree || busy} onClick={pay} data-testid="signup-pay-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Agree and start subscription</Button></div>
          <p className="text-xs text-slate-500">You'll enter your payment card on Stripe's secure page. Your card details never reach our servers.</p>
        </div>
      )}
      <DocDialog slug={doc} onClose={() => setDoc(null)} />
    </div>
  );
}

export function SignupDone() {
  const [sp] = useSearchParams();
  const api = useApi();
  const nav = useNavigate();
  const [state, setState] = useState("checking");
  useEffect(() => {
    let n = 0, stop = false;
    const poll = async () => {
      try {
        const r = await api("get", `/billing/session/${sp.get("session_id")}`);
        if (r.payment_status === "paid") { setState("paid"); localStorage.removeItem("ir_signup"); return; }
      } catch { /* retry */ }
      if (!stop && ++n < 15) setTimeout(poll, 2000); else setState("slow");
    };
    poll();
    return () => { stop = true; };
  }, [api, sp]);
  return (
    <div className="mx-auto max-w-xl px-5 py-16 text-center" data-testid="signup-done">
      {state === "checking" && <p className="text-slate-500"><Loader2 className="mx-auto mb-3 h-6 w-6 animate-spin" />Confirming your payment…</p>}
      {state === "slow" && <p>We're still waiting for confirmation from the payment provider. This page will be updated in your account shortly.</p>}
      {state === "paid" && (<>
        <h1 className="font-display text-4xl font-extrabold">You're all set</h1>
        <p className="mt-3 text-slate-600">We've emailed a confirmation with your key points and links to every legal document. Your signed Data Processing Agreement is in Settings, Legal.</p>
        <Button className="mt-8" onClick={() => nav("/welcome")} data-testid="signup-done-continue">Set up your pharmacy</Button>
      </>)}
    </div>
  );
}
